import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RepoService } from '../services/repo.service';
import { RfidPrices } from '../models/rfid-price';

export interface CustomPriceEntry {
  key: string;
  value: string;
}

@Component({
  selector: 'app-rfid-price',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner
  ],
  templateUrl: './rfid-price.component.html',
  styleUrl: './rfid-price.component.scss'
})
export class RfidPriceComponent implements OnInit {
  public readonly repoService = inject(RepoService);
  public readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  energyPrice = signal<string>('50 sek');
  flexiblePrice = signal<string>('100 sek');
  customPrices = signal<CustomPriceEntry[]>([]);

  originalPrices = signal<RfidPrices | null>(null);
  isLoading = signal<boolean>(true);
  isSaving = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  hasChanges = computed(() => {
    const orig = this.originalPrices();
    if (!orig) return false;

    if (this.energyPrice().trim() !== (orig.energy ?? '')) return true;
    if (this.flexiblePrice().trim() !== (orig.flexible ?? '')) return true;

    // Check custom fields
    const currentCustom = this.customPrices().filter(c => c.key.trim() !== '');
    const origCustomKeys = Object.keys(orig).filter(k => k !== 'energy' && k !== 'flexible');

    if (currentCustom.length !== origCustomKeys.length) return true;

    for (const c of currentCustom) {
      if (orig[c.key.trim()] !== c.value.trim()) return true;
    }

    return false;
  });

  previewPayload = computed(() => {
    const prices: Record<string, string> = {
      energy: this.energyPrice().trim(),
      flexible: this.flexiblePrice().trim()
    };
    for (const item of this.customPrices()) {
      if (item.key.trim()) {
        prices[item.key.trim()] = item.value.trim();
      }
    }
    return { prices };
  });

  ngOnInit(): void {
    this.repoService.listenToRfidPrices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (data) {
            this.energyPrice.set(data.energy ?? '50 sek');
            this.flexiblePrice.set(data.flexible ?? '100 sek');

            const custom: CustomPriceEntry[] = Object.entries(data)
              .filter(([key]) => key !== 'energy' && key !== 'flexible')
              .map(([key, value]) => ({ key, value: String(value) }));
            this.customPrices.set(custom);

            this.originalPrices.set(JSON.parse(JSON.stringify(data)));
          } else {
            // Default template if document does not exist yet in Firestore
            this.energyPrice.set('50 sek');
            this.flexiblePrice.set('100 sek');
            this.customPrices.set([]);
            this.originalPrices.set({ energy: '50 sek', flexible: '100 sek' });
          }
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('[RfidPriceComponent] Error loading RFID prices:', err);
          this.errorMessage.set('Failed to load RFID prices from Firestore.');
          this.isLoading.set(false);
        }
      });
  }

  addCustomField() {
    this.customPrices.update(list => [...list, { key: '', value: '' }]);
  }

  removeCustomField(index: number) {
    this.customPrices.update(list => list.filter((_, i) => i !== index));
  }

  updateCustomKey(index: number, newKey: string) {
    this.customPrices.update(list => {
      const updated = [...list];
      updated[index] = { ...updated[index], key: newKey };
      return updated;
    });
  }

  updateCustomValue(index: number, newValue: string) {
    this.customPrices.update(list => {
      const updated = [...list];
      updated[index] = { ...updated[index], value: newValue };
      return updated;
    });
  }

  resetForm() {
    const orig = this.originalPrices();
    if (orig) {
      this.energyPrice.set(orig.energy ?? '50 sek');
      this.flexiblePrice.set(orig.flexible ?? '100 sek');

      const custom: CustomPriceEntry[] = Object.entries(orig)
        .filter(([key]) => key !== 'energy' && key !== 'flexible')
        .map(([key, value]) => ({ key, value: String(value) }));
      this.customPrices.set(custom);
    }
  }

  async savePrices() {
    if (!this.energyPrice().trim() || !this.flexiblePrice().trim()) {
      this.snackBar.open('Please fill in both Energy and Flexible prices', 'Close', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
      return;
    }

    this.isSaving.set(true);

    const payload: RfidPrices = {
      energy: this.energyPrice().trim(),
      flexible: this.flexiblePrice().trim()
    };

    for (const item of this.customPrices()) {
      if (item.key.trim()) {
        payload[item.key.trim()] = item.value.trim();
      }
    }

    try {
      await this.repoService.updateRfidPrices(payload);
      this.originalPrices.set(JSON.parse(JSON.stringify(payload)));
      this.snackBar.open('RFID prices updated successfully!', 'Close', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
    } catch (e) {
      console.error('[RfidPriceComponent] Error updating prices:', e);
      this.snackBar.open('Error saving RFID prices!', 'Close', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
    } finally {
      this.isSaving.set(false);
    }
  }
}
